"use client";

/**
 * P6-12 · Compact flash-sale countdown.
 *
 * "Ends in 2d 03h 04m" for an active sale, "Starts in …" for a teaser. Reuses the
 * pure `countdownParts` helper (same as the hero PromoCountdown). Renders nothing
 * before mount (avoids a server/client time mismatch), when the sale has no
 * countdown, or once the target has passed. Decorative — `aria-hidden`.
 */
import { useEffect, useState } from "react";

import type { CatalogSaleDisplay } from "~/lib/promotions/catalog-sale";
import {
  countdownParts,
  type CountdownParts,
} from "~/server/content/blocks/promotion";

const pad = (n: number) => String(n).padStart(2, "0");

export function FlashSaleCountdown({
  sale,
  className,
}: {
  sale: CatalogSaleDisplay;
  className?: string;
}) {
  const [parts, setParts] = useState<CountdownParts | null>(null);
  const target = sale.showCountdown ? sale.countdownTarget : null;

  useEffect(() => {
    if (!target) return;
    const targetMs = new Date(target).getTime();
    if (Number.isNaN(targetMs)) return;
    const tick = () => setParts(countdownParts(targetMs, Date.now()));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [target]);

  if (!target || !parts) return null;

  const text =
    parts.days > 0
      ? `${parts.days}d ${pad(parts.hours)}h ${pad(parts.minutes)}m`
      : `${pad(parts.hours)}h ${pad(parts.minutes)}m ${pad(parts.seconds)}s`;

  return (
    <span
      aria-hidden
      className={className ?? "text-xs font-medium text-danger tabular-nums"}
    >
      {sale.teaser ? "Starts in" : "Ends in"} {text}
    </span>
  );
}
